import React, { useState } from 'react';
import { Icons } from '../../Icon.jsx';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import { ALL_STATES } from '../../pseudo-classes.js';
import { getPropertyDefinition } from '../../style-plugins/index.js';
import PropertyRow from './PropertyRow.jsx';

const StylePluginItem = ({ plugin, activeProps, ownProps, disabledProps, manualProps = [], onChange, onRemove, onToggleProperty, onRemoveProperty, onAddProperty, onCopyState, isOpen, onToggle, onDragStart, activeBreakpoint = 'default' }) => {
    const [showAddProperty, setShowAddProperty] = useState(false);
    const [propertySearch, setPropertySearch] = useState('');

    // Get all property keys from the plugin (now object-based)
    const allPropertyKeys = typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)
        ? Object.keys(plugin.properties)
        : plugin.properties || [];

    // Get visible properties (active/inherited, disabled, OR manually added)
    const visibleKeys = allPropertyKeys.filter(key => activeProps[key] !== undefined || disabledProps[key] !== undefined || manualProps.includes(key));

    // Get available properties for dropdown (exclude visible ones)
    const availableKeys = allPropertyKeys.filter(key => !visibleKeys.includes(key));

    const filteredAvailable = availableKeys.filter(key => {
        const def = getPropertyDefinition(key);
        const label = def?.label || key;
        return label.toLowerCase().includes(propertySearch.toLowerCase());
    });

    return (
        <div className="bg-white border-b border-slate-300 last:border-b-0">
                        <div 
                            className="px-3 py-2 flex justify-between items-center cursor-pointer bg-slate-100 hover:bg-slate-200 transition-colors group border-b border-slate-300" 
                            onClick={onToggle}
                            onMouseDown={onDragStart}
                        >                <div className="flex items-center gap-2 text-xs font-bold text-slate-700 select-none uppercase tracking-wider">
                    {isOpen ? <Icons.ChevronDown className="w-3 h-3 text-slate-500" /> : <Icons.ChevronRight className="w-3 h-3 text-slate-500" />}
                    <span>{plugin.label}</span>
                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <div className="w-auto relative" onClick={(e) => e.stopPropagation()}>
                                                        <DropdownSelect
                                                            options={ALL_STATES.map(s => ({ label: s.label, value: s.value }))}                                            onChange={(val) => {
                                                const selection = Array.isArray(val) ? val[0] : val;
                                                if (selection) onCopyState && onCopyState(plugin.id, selection.value);
                                            }}
                                            searchEnabled={true}
                                            showCheckbox={false}
                                            variant="admin"
                                            className="!p-0 !m-0"
                                            dropdownWidth="10rem"
                                            customTrigger={
                                                <div className="p-1 rounded border border-slate-300 hover:border-blue-400 hover:bg-white/50 text-slate-500 hover:text-blue-600 cursor-pointer flex items-center justify-center w-6 h-6 transition-colors group/sbtn" title="Copy Plugin to State">
                                                    <span className="font-bold text-[10px] leading-none">S</span>
                                                </div>
                                            }
                                        />
                                    </div>
                                    <button 
                                        onClick={(e) => { 
                                            e.stopPropagation();                            setShowAddProperty(!showAddProperty);
                            if (!isOpen) setIsOpen(true);
                        }}
                        className={`p-1 rounded hover:bg-white/50 ${showAddProperty ? 'text-blue-600 bg-white/50' : 'text-slate-500'}`}
                        title="Add Property"
                    >
                        <Icons.Plus className="w-4 h-4"/>
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemoveProperty && onRemoveProperty(plugin.id); onRemove && onRemove(); }} // Use correct remover
                        className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-white/50"
                        title="Remove Plugin"
                    >
                        <Icons.Trash className="w-3.5 h-3.5"/>
                    </button>
                </div>
            </div>

            {isOpen && (
                <div className="bg-white">
                    {/* Add Property Full-Width UI */}
                    {showAddProperty && (
                        <div className="p-2 bg-blue-50/50 border-b border-blue-100 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="flex items-center gap-2 bg-white border border-blue-200 rounded px-2 py-1 mb-2 shadow-sm focus-within:ring-1 focus-within:ring-blue-400 transition-all">
                                <Icons.Search className="w-3 h-3 text-gray-400" />
                                <input
                                    autoFocus
                                    className="flex-1 text-xs outline-none bg-transparent"
                                    placeholder={`Search ${plugin.label} properties...`}
                                    value={propertySearch}
                                    onChange={e => setPropertySearch(e.target.value)}
                                />
                                {propertySearch && (
                                    <button onClick={() => setPropertySearch('')}>
                                        <Icons.Remove className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                                    </button>
                                )}
                                <button
                                    onClick={() => { setShowAddProperty(false); setPropertySearch(''); }}
                                    className="text-[10px] font-bold text-gray-400 hover:text-red-500 ml-1 border-l pl-2"
                                >
                                    CANCEL
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto pr-1">
                                {filteredAvailable.length === 0 && (
                                    <div className="col-span-2 text-[10px] text-gray-400 italic text-center py-2">
                                        {availableKeys.length === 0 ? "All properties added" : "No matches found"}
                                    </div>
                                )}
                                {filteredAvailable.map(key => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onAddProperty(key);
                                            setShowAddProperty(false);
                                            setPropertySearch('');
                                        }}
                                        className="text-left px-2 py-1.5 text-[10px] bg-white border border-slate-200 rounded hover:border-blue-300 hover:bg-blue-50 transition-colors flex justify-between items-center group/item"
                                    >
                                        <span className="truncate">{getPropertyDefinition(key)?.label || key}</span>
                                        <Icons.Plus className="w-2.5 h-2.5 text-gray-300 group-hover/item:text-blue-500 shrink-0 ml-1" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="p-2 space-y-1">
                        {visibleKeys.length === 0 && !showAddProperty && (
                            <div className="text-[10px] text-gray-400 italic text-center py-4">
                                No properties added. Click the (+) button in the header to start.
                            </div>
                        )}
                        {visibleKeys.map(key => (
                            <PropertyRow
                                key={key}
                                propertyKey={key}
                                activeProps={activeProps}
                                ownProps={ownProps}
                                disabledProps={disabledProps}
                                manualProps={manualProps}
                                onChange={onChange}
                                onToggleProperty={onToggleProperty}
                                onRemoveProperty={onRemoveProperty}
                                activeBreakpoint={activeBreakpoint}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StylePluginItem;